// Los datos del producto como UNA lista de campos (ADR-2026-10-15-los-datos-del-producto-son-una-lista,
// ADR-2026-10-15-las-medidas-son-campos-como-los-demas): cada fila es un nombre y su valor, con
// su X para sacarla, y abajo "+ Campo" para sumar cualquiera. Las medidas son campos como los
// demás: están si se cargaron (o si una toma las exige) y se sacan con su X. Un campo que se
// llama como una medida (Alto, Ancho, Profundidad, Espesor, Peso) va a `productos.medidas`, con
// número; el resto, a las características (`variantes.atributos`). La unidad (cm, mm o m) es UNA
// para todas las medidas y se elige desde cualquiera: cambiarla en una las cambia todas y
// convierte los valores (`cambiarUnidad`).
import { Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { fusionarFicha, type FichaDeProducto } from '../../../../lib/fichaDeProducto';
import { parsearMedida, type Medidas, type UnidadMedida } from '../../../../lib/medidas';
import {
  cambiarUnidad,
  ETIQUETA_DE_MEDIDA,
  esPrincipal,
  filasDeMedidasIniciales,
  MEDIDAS_EN_ORDEN,
  medidaDelNombre,
  SUGERENCIAS_DE_CAMPO,
  type ClaveDeMedida,
} from './filasDelProducto';

const UNIDADES: UnidadMedida[] = ['cm', 'mm', 'm'];
const fila = 'grid grid-cols-[150px_1fr_auto] items-center gap-2';
const caja = 'w-full rounded-lg border border-black/[0.1] bg-white px-2.5 py-1.5 text-[13.5px] text-brand-black outline-none focus:border-brand-primary';

const textoDe = (v: number | null | undefined): string => (typeof v === 'number' && v > 0 ? String(Number(v.toFixed(4))).replace('.', ',') : '');

/** La unidad de un campo, adentro de su caja, a la derecha. */
const SelectorDeUnidad: React.FC<{ unidad: UnidadMedida; onCambiar: (u: UnidadMedida) => void; etiqueta: string }> = ({ unidad, onCambiar, etiqueta }) => (
  <select
    value={unidad}
    onChange={(e) => onCambiar(e.target.value as UnidadMedida)}
    aria-label={`Unidad de ${etiqueta}`}
    className="absolute right-1.5 top-1/2 -translate-y-1/2 cursor-pointer rounded-md bg-transparent py-0.5 pl-1.5 pr-1 text-[12.5px] font-semibold text-[#3C3C43]/60 outline-none hover:bg-black/[0.04] hover:text-brand-black"
  >
    {UNIDADES.map((u) => (
      <option key={u} value={u}>
        {u}
      </option>
    ))}
  </select>
);

/** El valor de una medida en la unidad del campo: se escribe libre ("12,5") y se guarda al dejar de escribir. */
const ValorDeMedida: React.FC<{
  valor: number | null;
  etiqueta: string;
  /** null = peso (siempre kg). */
  unidad: UnidadMedida | null;
  onUnidad: (u: UnidadMedida) => void;
  requerida: boolean;
  onCambiar: (n: number | null) => void;
}> = ({ valor, etiqueta, unidad, onUnidad, requerida, onCambiar }) => {
  const [texto, setTexto] = useState(textoDe(valor));
  useEffect(() => setTexto(textoDe(valor)), [valor]);
  return (
    <span className="relative block">
      <input
        value={texto}
        inputMode="decimal"
        onChange={(e) => setTexto(e.target.value)}
        onBlur={() => onCambiar(parsearMedida(texto))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        placeholder={requerida ? 'Requerida' : ''}
        aria-label={etiqueta}
        className={`${caja} pr-14 ${requerida && !texto.trim() ? 'border-[#F3C3BE] placeholder:text-[#B42318]/60' : ''}`}
      />
      {unidad ? (
        <SelectorDeUnidad unidad={unidad} onCambiar={onUnidad} etiqueta={etiqueta} />
      ) : (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12.5px] font-semibold text-[#3C3C43]/60">kg</span>
      )}
    </span>
  );
};

/** El valor de una característica: se edita en el lugar y se guarda al dejar de escribir. */
const ValorDeCaracteristica: React.FC<{ valor: string; etiqueta: string; onCambiar: (v: string) => void }> = ({ valor, etiqueta, onCambiar }) => {
  const [texto, setTexto] = useState(valor);
  useEffect(() => setTexto(valor), [valor]);
  return (
    <input
      value={texto}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => texto.trim() !== valor && onCambiar(texto)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      aria-label={etiqueta}
      className={caja}
    />
  );
};

const BotonQuitar: React.FC<{ nombre: string; onQuitar: () => void }> = ({ nombre, onQuitar }) => (
  <button
    type="button"
    onClick={onQuitar}
    aria-label={`Quitar ${nombre}`}
    className="grid h-7 w-7 place-items-center rounded-md text-[rgb(var(--brand-black-rgb)_/_0.4)] hover:bg-black/[0.04] hover:text-[#B42318]"
  >
    <X className="h-3.5 w-3.5" strokeWidth={2} />
  </button>
);

export interface CamposDelProductoProps {
  medidas: Medidas | null;
  ficha: FichaDeProducto;
  /** Una toma del producto exige medidas (la ficha de medidas): alto, ancho y profundidad se ven aunque estén vacías. */
  exigeMedidas: boolean;
  onMedidas: (medidas: Medidas) => void;
  onFicha: (ficha: FichaDeProducto) => void;
}

export const CamposDelProducto: React.FC<CamposDelProductoProps> = ({ medidas, ficha, exigeMedidas, onMedidas, onFicha }) => {
  // Una sola unidad para todas las medidas; se cambia desde cualquiera y convierte todas.
  const unidad: UnidadMedida = medidas?.unidad ?? 'cm';
  const elegirUnidad = (u: UnidadMedida) => {
    if (u !== unidad) onMedidas(cambiarUnidad(medidas, u));
  };

  // Las filas de medidas a la vista: no se van mientras se borra el número, sólo con su X.
  const [filas, setFilas] = useState<ClaveDeMedida[]>(() => filasDeMedidasIniciales(medidas, exigeMedidas));
  useEffect(() => {
    const deben = filasDeMedidasIniciales(medidas, exigeMedidas);
    setFilas((prev) => (deben.every((k) => prev.includes(k)) ? prev : [...new Set([...prev, ...deben])]));
  }, [medidas, exigeMedidas]);

  const [nombre, setNombre] = useState('');
  const [valor, setValor] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  const conMedida = (k: ClaveDeMedida, n: number | null) => onMedidas({ ...(medidas ?? { unidad }), unidad, [k]: n });
  const ordenadas = MEDIDAS_EN_ORDEN.filter((k) => filas.includes(k));
  const medidaNueva = medidaDelNombre(nombre);

  const sumar = () => {
    setAviso(null);
    if (medidaNueva) {
      const n = parsearMedida(valor);
      if (valor.trim() && n === null) {
        setAviso(`${ETIQUETA_DE_MEDIDA[medidaNueva]} tiene que ser un número.`);
        return;
      }
      setFilas((prev) => (prev.includes(medidaNueva) ? prev : [...prev, medidaNueva]));
      if (n !== null) conMedida(medidaNueva, n);
    } else {
      if (!valor.trim()) return;
      onFicha(fusionarFicha(ficha, [{ clave: nombre, valor }], { origen: 'usuario', sobrescribir: true }).ficha);
    }
    setNombre('');
    setValor('');
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[13px] font-semibold text-brand-black">Datos del producto</p>

      {(ordenadas.length > 0 || ficha.campos.length > 0) && (
        <ul className="flex flex-col gap-1.5">
          {ordenadas.map((k) => {
            const requerida = exigeMedidas && esPrincipal(k);
            return (
              <li key={k} className={fila}>
                <span className="truncate text-[13.5px] text-[#3C3C43]/70">{ETIQUETA_DE_MEDIDA[k]}</span>
                <ValorDeMedida
                  valor={medidas?.[k] ?? null}
                  etiqueta={ETIQUETA_DE_MEDIDA[k]}
                  unidad={k === 'peso_kg' ? null : unidad}
                  onUnidad={elegirUnidad}
                  requerida={requerida}
                  onCambiar={(n) => conMedida(k, n)}
                />
                {/* Una medida que una toma exige no se saca: su X sólo la vacía (y sólo si tiene valor). */}
                {requerida ? (
                  typeof medidas?.[k] === 'number' && (medidas[k] as number) > 0 ? (
                    <BotonQuitar nombre={ETIQUETA_DE_MEDIDA[k]} onQuitar={() => conMedida(k, null)} />
                  ) : (
                    <span className="h-7 w-7" aria-hidden="true" />
                  )
                ) : (
                  <BotonQuitar
                    nombre={ETIQUETA_DE_MEDIDA[k]}
                    onQuitar={() => {
                      setFilas((prev) => prev.filter((x) => x !== k));
                      conMedida(k, null);
                    }}
                  />
                )}
              </li>
            );
          })}
          {ficha.campos.map((c) => {
            const etiqueta = c.clave ? c.clave[0].toUpperCase() + c.clave.slice(1) : 'Característica';
            return (
              <li key={c.clave || `=${c.valor}`} className={fila}>
                <span className="truncate text-[13.5px] text-[#3C3C43]/70" title={etiqueta}>
                  {etiqueta}
                </span>
                <ValorDeCaracteristica
                  valor={c.valor}
                  etiqueta={etiqueta}
                  onCambiar={(v) =>
                    onFicha({
                      campos: v.trim()
                        ? ficha.campos.map((x) => (x === c ? { ...x, valor: v.trim(), origen: 'usuario', actualizado: new Date().toISOString() } : x))
                        : ficha.campos.filter((x) => x !== c),
                    })
                  }
                />
                <BotonQuitar nombre={c.clave || c.valor} onQuitar={() => onFicha({ campos: ficha.campos.filter((x) => x !== c) })} />
              </li>
            );
          })}
        </ul>
      )}

      {/* Sumar un campo: el nombre sugiere las medidas y las características típicas. */}
      <div className={fila}>
        <input value={nombre} onChange={(e) => setNombre(e.target.value)} list="campos-del-producto" placeholder="Campo (opcional)" aria-label="Nombre del campo" className={caja} />
        <span className="relative block">
          <input
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') sumar();
            }}
            placeholder="Valor"
            aria-label="Valor del campo"
            className={`${caja} ${medidaNueva ? 'pr-14' : ''}`}
          />
          {medidaNueva &&
            (medidaNueva === 'peso_kg' ? (
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12.5px] font-semibold text-[#3C3C43]/60">kg</span>
            ) : (
              <SelectorDeUnidad unidad={unidad} onCambiar={elegirUnidad} etiqueta={ETIQUETA_DE_MEDIDA[medidaNueva]} />
            ))}
        </span>
        <button
          type="button"
          onClick={sumar}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.095)] py-1.5 px-3 text-[13px] font-semibold text-brand-primary hover:bg-[rgb(var(--brand-primary-rgb)_/_0.165)]"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" />
          Campo
        </button>
      </div>
      <datalist id="campos-del-producto">
        {SUGERENCIAS_DE_CAMPO.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      {aviso && <p className="text-[12.5px] text-[#B42318]">{aviso}</p>}
    </div>
  );
};
